// M004-F0800 (WebUI #266 slice 2): the Statement view on statement.get, customer scope. Whole history from the Ledger; the period is
// chosen here and applied by periodView. Balances are the Ledger's balance_minor, shown as sent (never recomputed).
// Errors and refusals show a generic sentence only: the server message never reaches the page.
// Lazy (like the advice panel): the section starts collapsed and statement.get is sent only when it is opened. The answer is cached for
// the page session; the period picker filters it client-side; "Refresh" inside the open section re-reads it.
import React, { useEffect, useRef, useState } from 'react';
import { statementApi } from '../../services/statementApi';
import { formatAmount, formatBalance, monthPreset, periodView } from './statementGetModel';

export const STATEMENT_VIEW_LOAD_FAILED = 'Your statement could not be loaded. Try again later.';
export const STATEMENT_VIEW_EMPTY = 'There are no transactions on your statement yet.';
export const STATEMENT_VIEW_NO_ROWS = 'No transactions in this period.';
export const STATEMENT_VIEW_BAD_RANGE = 'The from date must be on or before the to date.';

const BODY_ID = 'statement-view-body';

const PRESETS = [
  { id: 'all', label: 'All' },
  { id: 'this', label: 'This month' },
  { id: 'last', label: 'Last month' },
  { id: 'custom', label: 'Custom' },
];

function rangeFor(preset, custom, today) {
  if (preset === 'this' || preset === 'last') return monthPreset(preset, today);
  if (preset === 'custom') return { from: custom.from || null, to: custom.to || null };
  return { from: null, to: null };
}

export default function StatementView({ orgId, customerId, today }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ status: 'loading', lines: [] });
  const [reloadKey, setReloadKey] = useState(0);
  const [preset, setPreset] = useState('all');
  const [custom, setCustom] = useState({ from: '', to: '' });
  // The request key the cached answer (or the in-flight request) belongs to. A key change (Refresh, another customer or org) fetches again.
  const fetchedFor = useRef(null);

  useEffect(() => {
    if (!open) return;
    const key = `${orgId}|${customerId}|${reloadKey}`;
    if (fetchedFor.current === key) return;
    fetchedFor.current = key;
    setState({ status: 'loading', lines: [] });
    statementApi.getStatement(orgId, customerId)
      .then((response) => {
        if (fetchedFor.current !== key) return;
        const lines = response?.data?.data?.lines;
        setState({ status: 'ready', lines: Array.isArray(lines) ? lines : [] });
      })
      .catch(() => {
        if (fetchedFor.current === key) setState({ status: 'error', lines: [] });
      });
  }, [open, orgId, customerId, reloadKey]);

  const heading = <h2 id="statement-view-heading">Statement</h2>;
  const toggle = (
    <button type="button" aria-expanded={open} aria-controls={BODY_ID} onClick={() => setOpen((value) => !value)}>
      {open ? 'Hide statement' : 'Show statement'}
    </button>
  );

  let body = null;
  if (open) {
    if (state.status === 'loading') {
      body = <p role="status" data-testid="statement-view-loading">Loading statement…</p>;
    } else if (state.status === 'error') {
      body = (
        <>
          <p role="alert" data-testid="statement-view-error">{STATEMENT_VIEW_LOAD_FAILED}</p>
          <button type="button" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
        </>
      );
    } else if (state.lines.length === 0) {
      body = <p data-testid="statement-view-empty">{STATEMENT_VIEW_EMPTY}</p>;
    } else {
      body = renderStatement();
    }
  }

  function renderStatement() {
    const range = rangeFor(preset, custom, today);
    const badRange = preset === 'custom' && range.from !== null && range.to !== null && range.from > range.to;
    const view = periodView(state.lines, range);
    return (
      <>
        <button type="button" onClick={() => setReloadKey((k) => k + 1)}>Refresh</button>
        <div role="group" aria-label="Period" data-testid="statement-period">
          {PRESETS.map((item) => (
            <button key={item.id} type="button" aria-pressed={preset === item.id} onClick={() => setPreset(item.id)}>{item.label}</button>
          ))}
        </div>
        {preset === 'custom' ? (
          <div data-testid="statement-custom-range">
            <label>From <input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} /></label>
            <label>To <input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} /></label>
            {badRange ? <p role="alert" data-testid="statement-bad-range">{STATEMENT_VIEW_BAD_RANGE}</p> : null}
          </div>
        ) : null}
        {badRange ? null : (
          <table data-testid="statement-table">
            <caption className="visually-hidden">Statement lines in the selected period, in ledger order</caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Description</th>
                <th scope="col">Amount</th>
                <th scope="col">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr data-testid="statement-opening">
                <td />
                <td>Opening balance</td>
                <td />
                <td data-testid="statement-opening-balance">{formatBalance(view.opening_minor)}</td>
              </tr>
              {view.rows.map((line, index) => (
                // The Ledger can answer two lines with one transaction_id (a receipt split across invoices), so the index keys the row.
                <tr key={index} data-testid="statement-line">
                  <td>{line.date}</td>
                  <td>{line.description}</td>
                  <td data-testid="statement-line-amount">{formatAmount(line.amount_minor)}</td>
                  <td data-testid="statement-line-balance">{formatBalance(line.balance_minor)}</td>
                </tr>
              ))}
              <tr data-testid="statement-closing">
                <td />
                <td>Closing balance</td>
                <td />
                <td data-testid="statement-closing-balance">{formatBalance(view.closing_minor)}</td>
              </tr>
            </tbody>
          </table>
        )}
        {!badRange && view.rows.length === 0 ? <p data-testid="statement-no-rows">{STATEMENT_VIEW_NO_ROWS}</p> : null}
      </>
    );
  }

  return (
    <section aria-labelledby="statement-view-heading" data-testid="statement-view" className="statement-view">
      {heading}
      {toggle}
      <div id={BODY_ID} hidden={!open}>
        {body}
      </div>
    </section>
  );
}
